import {
  basketAddItemsResultsEvent,
  basketAddPromoCodeEvent,
  basketItemRemoved,
  basketRemovePromoCodeEvent,
  orderProceedEvent,
  productBoxClickEvent,
  changePaymentMethod as sendChangePaymentMethodEvent,
} from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import type { BasketResponseAction } from "~/store/basket/actions"
import { BasketActions } from "~/store/basket/constants"
import { getLastItemsFromBasket } from "~/store/basket/utils"
import { BonusProductActionsType } from "~/store/bonus/actions-product"
import { BonusUpsellActionType } from "~/store/bonus/actions-upsell"
import type { PaymentSuccessAction } from "~/store/order/actions"
import { OrderActionType } from "~/store/order/constants"
import type { QuickCheckoutItemsAddedAction } from "~/store/quick-checkout/actions"
import { QuickCheckoutActionType } from "~/store/quick-checkout/constants"

const middleware =
  () =>
  (next) =>
  (
    action:
      | BasketResponseAction
      | PaymentSuccessAction
      | QuickCheckoutItemsAddedAction
  ) => {
    if (
      action.type === BasketActions.BASKET_CHANGE_PAYMENT &&
      !action.noSendEvent
    ) {
      sendChangePaymentMethodEvent(
        action.value,
        action.payment_type,
        action.system_id,
        action.basketOrderedItems,
        action.cart_id
      )
    }

    if (action.type === BasketActions.BASKET_ITEM_REMOVED) {
      basketItemRemoved(
        action.cart_id,
        action.basketOrderedItems,
        action.id,
        action.item_id ? action.item_id : action.name,
        action.name,
        action.price,
        action.quantity,
        action.affiliation,
        action.item_category,
        action.item_variant
      )
    }

    if (
      action.type === OrderActionType.PAYMENT_SUCCESS ||
      action.type === QuickCheckoutActionType.PAYMENT_SUCCESS
    ) {
      const paymentMethod = action.payment_method

      const systemId = action.payment_methods.find(
        (method) => method.id === paymentMethod
      )?.system_id

      orderProceedEvent(
        action.result.number,
        action.basket.total_net_price,
        action.basketOrderedItems,
        action.basket.token ?? "",
        action.userRegistered,
        action.accountType,
        paymentMethod,
        systemId
      )
    }

    if (action.type === BasketActions.BASKET_SUCCESS) {
      basketAddItemsResultsEvent(
        action.result,
        action.basketOrderedItems,
        action.source ?? undefined
      )
    }

    if (action.type === QuickCheckoutActionType.ITEMS_ADDED) {
      // Skip if the basket carries no freshly added items — the quick-purchase
      // basket is new, hence no index offset.
      if (!isEmpty(getLastItemsFromBasket(action.basket))) {
        basketAddItemsResultsEvent(action.basket, [])
      }
    }

    if (action.type === BasketActions.BASKET_ADD_PROMO_CODE) {
      basketAddPromoCodeEvent(action.result)
    }

    if (action.type === BasketActions.BASKET_REMOVE_PROMO_CODE) {
      basketRemovePromoCodeEvent(action.result)
    }

    if (action.type === BonusProductActionsType.BONUS_CHANGE_PERIOD_CLICK) {
      productBoxClickEvent({
        productbox_type: "select",
        productbox_option: "period",
        productbox_checked_item: action.period.id,
        productbox_time: action.period.period_name,
      })
      window.jentisDataLayer?.pushProductBoxClickEvent({
        productbox_type: "select",
        productbox_option: "period",
        productbox_checked_item: action.period.id,
        productbox_time: action.period.period_name,
      })
    }

    if (
      action.type === BonusUpsellActionType.BONUS_UPSELL_CHANGE_PERIOD_CLICK
    ) {
      productBoxClickEvent({
        productbox_type: "select",
        productbox_option: "period",
        productbox_checked_item: action.upsell.planId,
      })
      window.jentisDataLayer?.pushProductBoxClickEvent({
        productbox_type: "select",
        productbox_option: "period",
        productbox_checked_item: action.upsell.planId,
      })
    }

    return next(action)
  }

export default middleware
